import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser } from 'lucide-react';

/**
 * HTML5-canvas signature pad. Calls onChange(dataUrl|null) as user signs/clears.
 */
export const SignaturePad = ({ onChange, height = 180 }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, [height]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (empty) setEmpty(false);
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-2">
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full h-full touch-none cursor-crosshair"
          style={{ height }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{empty ? 'Sign with your mouse or finger above' : 'Signature captured'}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clear}
          data-testid="signature-clear-btn"
        >
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
